<template>
  <div class="container px-4">
    <br class="mt-4" />
    <h1 class="title">{{ t("CL_WITHDRAWAL.CLABE_SETUP_TITLE") }}</h1>
    <div v-if="editMode" class="mb-3">
      {{ t("CL_WITHDRAWAL.UPDATE_BANK_ACCOUNT_DISCLAIMER") }}
    </div>

    <TextInput
      placeholder="xxx xxx xxxxxxxxxxx x"
      v-model:value="bankAccountInputValue"
      @focusin="onFocusTextInput"
      :maxLength="21"
      :error="bankAccountInputError"
      :fill="true"
    />
    <label v-if="bankAccountInputError" class="error-label">
      <strong>{{ t("ERROR") }}</strong> - {{ t("CL_WITHDRAWAL.CLABE_ERROR") }}
    </label>
    <SelectInput
      :placeholder="t('CL_WITHDRAWAL.SELECT_BANK')"
      :options="bankNameOptions"
      :fill="true"
      v-model:value="bankNameInputValue"
    />
    <CollapseCard
      v-if="!editMode"
      :title="t('CL_WITHDRAWAL.WHY_CLABE')"
      :fill="true"
    >
      <div>{{ t("CL_WITHDRAWAL.WHY_CLABE_CONTENT") }}</div>
    </CollapseCard>

    <CollapseCard
      :title="t('CL_WITHDRAWAL.IMPORTANT_NOTE_TITLE')"
      :fill="true"
      :collapsable="false"
      titleIcon="fa-regular fa-note-sticky"
      :boldTitle="true"
    >
      <div>
        <p class="text-black">
          {{
            t(
              windowPickupDisabledReason === "OPEN_MARKET_CUSTOMER"
                ? "CL_WITHDRAWAL.IMPORTANT_NOTE_DESCRIPTION_2"
                : "CL_WITHDRAWAL.IMPORTANT_NOTE_DESCRIPTION"
            )
          }}
        </p>
      </div>
      <img
        v-if="windowPickupDisabledReason === 'OPEN_MARKET_CUSTOMER'"
        class="w-100"
        :src="useAsset('img/credit-line/whitelist_banks.png')"
        alt="banks"
      />
    </CollapseCard>

    <div class="floating-btn">
      <Button
        id="btn-continue"
        class="text-start w-100 p-3"
        :class="!bankNameInputValue || !bankAccountInputValue ? 'disabled' : ''"
        @click="clickBtn"
        :disabled="!bankNameInputValue || !bankAccountInputValue"
      >
        <div class="text-white text-center my-auto fw-bold">
          <span>{{ t("CL_WITHDRAWAL.CONFIRM_BANK_ACCOUNT") }}</span>
        </div>
      </Button>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, ref, watch } from "vue";
import { useI18n } from "@/apps/credit-line/composables/useI18n";
import { clabe } from "clabe-validator";
import TextInput from "@/apps/credit-line/components/TextInput.vue";
import SelectInput from "@/apps/credit-line/components/SelectInput.vue";
import CollapseCard from "@/apps/credit-line/components/CollapseCard.vue";
import Button from "@/apps/credit-line/components/Button.vue";
import { Service } from "@/apps/credit-line/services/network.service";
import { IBankOptionInfo } from "@/apps/credit-line/models/IBankOptionInfo";
import { ErrorHandler } from "@/apps/credit-line/helpers/errorHandlerWithLogger.helper";
import logAnalyticsEvent from "@/apps/credit-line/services/google-analytics-events.service";
import AnalyticEvents from "@/shared-helpers/google-analytics-events.helper";
import useAsset from "@/apps/credit-line/helpers/useAsset";

export default defineComponent({
  name: "BankAccountSetup",
  components: { Button, TextInput, SelectInput, CollapseCard },
  emits: ["next", "setScreenTitle"],
  props: {
    editMode: {
      type: Boolean,
      default: false,
    },
    windowPickupDisabledReason: String,
  },
  setup() {
    const { t } = useI18n();
    type BankInputOption = Omit<IBankOptionInfo, "participantKey"> & {
      text: string;
    }; // { key: string, text: string }
    const CreditLineService = new Service();
    const bankAccountInputValue = ref<string>("");
    const bankAccountInputError = ref<boolean>(false);
    const bankNameInputValue = ref<string>("");
    const bankNameOptions = ref<BankInputOption[]>([]);
    const loading = ref<boolean>(false);

    onMounted(() => {
      logAnalyticsEvent(
        AnalyticEvents.CreditLine.ClabeScreenView,
        "Withdrawal"
      );
      CreditLineService.getAllBanks()
        .then((res) => {
          const parsedArray = res.data?.map((e) => ({
            text: e.participantKey,
            ...e,
          }));
          parsedArray.sort((a, b) => {
            if (a.text < b.text) {
              return -1;
            }
            if (a.text > b.text) {
              return 1;
            }
            return 0;
          });
          bankNameOptions.value = parsedArray;
        })
        .catch(ErrorHandler.handleNetworkErrorAndLog);
    });

    watch(bankAccountInputValue, (newValue) => {
      if (!newValue) {
        bankAccountInputValue.value = "";
        return;
      }
      // Parse input value every time we read a new digit
      // The expected format is "xxx xxx xxxxxxxxxxx x"
      let parsedValue = newValue.replace(/[^\d\s]/g, "");
      parsedValue = newValue.replace(/^(\d{3})(\d)/g, "$1 $2");
      parsedValue = parsedValue.replace(/^(\d{3}\s)(\d{3})(\d)/g, "$1$2 $3");
      parsedValue = parsedValue.replace(
        /^(\d{3}\s)(\d{3}\s)(\d{11})(\d)/g,
        "$1$2$3 $4"
      );
      bankAccountInputValue.value = parsedValue;

      // Check if new value matches the final expected format
      // If so, run Clabe Validator to identify bank name
      if (/^(\d{3}\s)(\d{3}\s)(\d{11}\s)(\d)$/g.test(parsedValue)) {
        bankAccountInputError.value = false;
        const clabeCheck = clabe.validate(parsedValue.replaceAll(" ", ""));
        const bankName = clabeCheck.tag || "";
        // TODO: Should verify against key
        if (bankNameOptions?.value.findIndex((e) => e.text === bankName) >= 0) {
          bankNameInputValue.value =
            bankNameOptions.value.find((e) => e.text === bankName)?.key ?? "";
        }
      }
    });

    const onFocusTextInput = () => {
      logAnalyticsEvent(AnalyticEvents.CreditLine.ClabeTyping, "Withdrawal");
    };

    return {
      t,
      loading,
      bankNameOptions,
      bankNameInputValue,
      bankAccountInputError,
      bankAccountInputValue,
      onFocusTextInput,
    };
  },
  methods: {
    useAsset,
    clickBtn() {
      this.loading = true;
      this.bankAccountInputError = false;
      const parsedClabe = this.bankAccountInputValue.replaceAll(" ", "");
      const clabeCheck = clabe.validate(parsedClabe);

      if (!clabeCheck.ok) {
        this.loading = false;
        this.bankAccountInputError = true;
        // End process before calling our API
        return;
      }

      let bankInfo = this.bankNameOptions.find(
        (e) => e.key === this.bankNameInputValue
      );

      this.$emit("next", {
        clabe: parsedClabe,
        bank: { participantKey: bankInfo?.text, ...bankInfo },
      });
    },
  },
  mounted() {
    if (!this.editMode) {
      this.$emit("setScreenTitle", this.t("CL_WITHDRAWAL.LINK_BANK_ACCOUNT"));
    } else {
      this.$emit("setScreenTitle", this.t("CL_WITHDRAWAL.UPDATE_BANK_ACCOUNT"));
    }
  },
});
</script>

<style lang="scss" scoped>
@import "@/assets/scss/_constants.scss";
.title {
  font-size: 18px;
  line-height: 1.3;
  margin-bottom: 20px;
}
.bottom {
  margin-top: 100%;
}
.disabled {
  filter: grayscale(60%) saturate(50%);
  pointer-events: none;
}
.btn-text {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.error-label {
  color: $pj-color-red;
  font-size: 14px;
  margin-bottom: 20px;
}
.floating-btn-placeholder,
.floating-btn {
  height: 80px;
  width: 100%;
}
.floating-btn {
  background-color: white;
  left: 0;
  bottom: 0;
  z-index: 200;
}
</style>
